import { CUSTOMER_APIS } from "@/api_framework/api_config";
import { ICustomerAddress } from "@/api_framework/api_modals/Addresses";
import {
  ICustomerAddresses,
  ICustomerDetailsCustomerProfile,
  ICustomerDetailsSearchResponse,
} from "@/api_framework/api_modals/Users";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import {
  setCustomerAddresses,
  setCustomerDetails,
  setCustomerMembershipDetails,
  setCustomerMembershipStatus,
  setCustomerPrimaryAddress,
  setPetDetails,
} from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import axios, { AxiosError, AxiosResponse } from "axios";
import curlirize from "axios-curlirize";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useConfiguration } from "../../useConfiguration";
import { useCurlLogger } from "../../useCurlLogger";

curlirize(axios, () => {});

export const useCustomerDetailsAPI = () => {
  const configuration = useConfiguration();
  const dispatch = useAppDispatch();
  const { sendCurl, removeCurl } = useCurlLogger();

  const findPrimaryAddress = (addresses: ICustomerAddress[]) => {
    if (addresses.length === 0) return null;
    const homeAddresses = addresses?.filter((address) => address.label === "1");
    if (homeAddresses?.length > 0) {
      return homeAddresses[0];
    } else {
      return null;
    }
  };

  const setCustomerDetailsData = (
    customerDetails: ICustomerDetailsCustomerProfile,
    addresses: ICustomerAddresses,
    membershipStatus: boolean,
    membershipDetail: string,
  ) => {
    dispatch(
      setCustomerMembershipStatus({
        status: membershipStatus,
      }),
    );
    dispatch(
      setCustomerMembershipDetails({
        membershipDetails: membershipDetail,
      }),
    );
    dispatch(
      setCustomerDetails({
        customerDetails: customerDetails,
      }),
    );
    dispatch(
      setCustomerAddresses({
        addresses: addresses,
      }),
    );
    const primaryAddress = findPrimaryAddress(addresses);
    dispatch(setCustomerPrimaryAddress(primaryAddress));
  };
  const toggleLoading = (toggleState: boolean) => {
    dispatch(
      setLoading({
        key: "isCustomerDetailsFetchInProgress",
        status: toggleState,
      }),
    );
  };

  const execute = useCallback(
    async (userId: string) => {
      toggleLoading(true);
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        await axios
          .get(CUSTOMER_APIS?.CUSTOMER_DETAILS?.baseURL ?? "", {
            params: {
              user_id: userId,
            },

            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          })
          .then((res: AxiosResponse<ICustomerDetailsSearchResponse>) => {
            toggleLoading(false);
            removeCurl();
            if (res.data.status === true) {
              setCustomerDetailsData(
                res.data.data.customer_details,
                res.data.data.address,
                res.data.data.membership_status,
                res.data.data.membership_status_detailed,
              );
              dispatch(
                setPetDetails({
                  petDetails: null,
                }),
              );
            } else {
              setCustomerDetailsData(null, [], false, null);
              dispatch(
                setPetDetails({
                  petDetails: null,
                }),
              );
            }
          })
          .catch((e: AxiosError) => {
            toggleLoading(false);
            sendCurl(e.config["curlCommand"]);
            setCustomerDetailsData(null, [], false, null);
            dispatch(
              setPetDetails({
                petDetails: null,
              }),
            );
            if (e.response.status === 400) {
              toast.error("Cannot Fetch User Details.");
            }
          });
      } catch (e: any) {
        toast.error("Server Error: " + e.message);
      }
    },
    [dispatch],
  );
  return execute;
};
